import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../constants/appConstants';

// Time entry from Bamboo
interface BambooTimeEntry {
  startTime: string;
  endTime: string | null;
  startTimestamp: number;
  endTimestamp: number | null;
  duration: number; // in seconds
  isActive: boolean;
}

interface BambooStatus {
  linked: boolean;
  isClockedIn: boolean;
  clockInTime: string | null;
  clockInTimestamp: number | null;
  todayTotalSeconds: number;
  entries: BambooTimeEntry[];
  serverTime: number;
}

interface UseBambooStatusResult {
  status: BambooStatus | null;
  loading: boolean;
  error: string | null;
  linked: boolean;
  clockIn: () => Promise<boolean>;
  clockOut: () => Promise<boolean>;
  refetch: () => Promise<void>;
  elapsedSeconds: number;
  todayTotalSeconds: number;
}

// Refresh interval in milliseconds (30 seconds)
const REFRESH_INTERVAL = 30 * 1000;

/**
 * Hook to manage Bamboo clock in/out status with live elapsed time
 * Now fetches actual time entries from Bamboo and refreshes periodically
 * @param employeeId The employee ID to check status for
 */
export const useBambooStatus = (employeeId: string | null): UseBambooStatusResult => {
  const [status, setStatus] = useState<BambooStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [clockInTimestamp, setClockInTimestamp] = useState<number | null>(null);
  const [completedTodaySeconds, setCompletedTodaySeconds] = useState(0);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchStatus = useCallback(async (showLoading = true) => {
    if (!employeeId) return;
    
    if (showLoading) {
      setLoading(true);
    }
    setError(null);

    try {
      const response = await axios.get<BambooStatus>(
        `${API_URL}/intranet/bamboo/status/${employeeId}`,
        { withCredentials: true }
      );
      
      const data = response.data;
      setStatus(data);
      
      // Calculate completed time from entries (excluding active entry)
      const completedTime = data.entries
        .filter(e => !e.isActive)
        .reduce((acc, e) => acc + e.duration, 0);
      setCompletedTodaySeconds(completedTime);
      
      // Handle active session
      if (data.isClockedIn && data.clockInTimestamp) {
        const serverNow = data.serverTime;
        const clientNow = Math.floor(Date.now() / 1000);
        const timeDiff = clientNow - serverNow;
        
        // Adjust the clock-in timestamp to client time
        const adjustedTimestamp = data.clockInTimestamp + timeDiff;
        setClockInTimestamp(adjustedTimestamp);
        
        // Calculate initial elapsed time for current session
        const elapsed = clientNow - adjustedTimestamp;
        setElapsedSeconds(Math.max(0, elapsed));
      } else {
        setClockInTimestamp(null);
        setElapsedSeconds(0);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to get Bamboo status');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [employeeId]);

  // Timer effect to update elapsed time every second when clocked in
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (status?.isClockedIn && clockInTimestamp) {
      interval = setInterval(() => {
        const now = Math.floor(Date.now() / 1000);
        const elapsed = now - clockInTimestamp;
        setElapsedSeconds(Math.max(0, elapsed));
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [status?.isClockedIn, clockInTimestamp]);

  // Initial fetch and periodic refresh
  useEffect(() => {
    if (employeeId) {
      fetchStatus();
      
      // Set up periodic refresh to catch external changes
      refreshIntervalRef.current = setInterval(() => {
        fetchStatus(false); // Don't show loading spinner for background refresh
      }, REFRESH_INTERVAL);
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [employeeId, fetchStatus]);

  const clockIn = useCallback(async (): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      await axios.post(
        `${API_URL}/intranet/bamboo/clock-in`,
        {},
        { withCredentials: true }
      );
      
      // Set clock-in timestamp to now and update status immediately
      const now = Math.floor(Date.now() / 1000);
      setClockInTimestamp(now);
      setElapsedSeconds(0);
      setStatus(prev => prev ? { 
        ...prev, 
        isClockedIn: true, 
        clockInTimestamp: now,
        clockInTime: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
      } : {
        linked: true,
        isClockedIn: true,
        clockInTime: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
        clockInTimestamp: now,
        todayTotalSeconds: completedTodaySeconds,
        entries: [],
        serverTime: now
      });
      
      // Refetch after a short delay to get the actual entry from server
      setTimeout(() => fetchStatus(false), 2000);
      
      return true;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to clock in');
      return false;
    } finally {
      setLoading(false);
    }
  }, [completedTodaySeconds, fetchStatus]);

  const clockOut = useCallback(async (): Promise<boolean> => {
    setLoading(true);
    setError(null);
    
    // Store the elapsed time before resetting
    const finalElapsed = elapsedSeconds;

    try {
      await axios.post(
        `${API_URL}/intranet/bamboo/clock-out`,
        {},
        { withCredentials: true }
      );
      
      // Update completed time to include this session
      setCompletedTodaySeconds(prev => prev + finalElapsed);
      
      // Reset current session
      setClockInTimestamp(null);
      setElapsedSeconds(0);
      setStatus(prev => prev ? { 
        ...prev, 
        isClockedIn: false, 
        clockInTimestamp: null, 
        clockInTime: null 
      } : null);
      
      // Refetch after a short delay to get the updated entries from server
      setTimeout(() => fetchStatus(false), 2000);
      
      return true;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to clock out');
      return false;
    } finally {
      setLoading(false);
    }
  }, [elapsedSeconds, fetchStatus]);

  // Calculate total time today (completed sessions + current active session)
  const todayTotalSeconds = completedTodaySeconds + (status?.isClockedIn ? elapsedSeconds : 0);

  return {
    status,
    loading,
    error,
    linked: status?.linked ?? false,
    clockIn,
    clockOut,
    refetch: fetchStatus,
    elapsedSeconds,
    todayTotalSeconds
  };
};

export default useBambooStatus;
